import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import getWalletBalance from '../../utils/react-query/query-options/get-wallet-balance';
import canAccessWallet from '../../utils/wallet-access';
import useLoggedUser from '../use-logged-user';

const toAmount = (value?: number | string | null): number | undefined => {
  // `Number(null)` and `Number('')` are `0`, which would turn "the backend has
  // no number for us" into a balance of zero — the one thing wallet UI must not
  // invent. Only an actual value gets converted.
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const amount = Number(value);

  return Number.isFinite(amount) ? amount : undefined;
};

/**
 * The wallet field that represents money the company can actually spend on the
 * `wallet-balance` endpoint payload, which is `accounting` — **not** the
 * documented `available`. Everything that shows a spendable amount from that
 * endpoint goes through here, so a future change of field is one edit.
 * Returns `undefined` when the payload carries no usable number, and callers
 * then render nothing rather than a wrong or misleading balance.
 */
export const getPurchaseBalance = (
  balance?: TWalletBalance | null
): number | undefined => toAmount(balance?.available);

/**
 * Whether the logged user may see wallet UI. Use this (never the raw staff
 * flags) so the rollout rules stay in `utils/wallet-access`.
 */
export const useWalletAccess = (): boolean => {
  const user = useLoggedUser();

  return canAccessWallet(user);
};

/**
 * Wallet balance for the logged company, read straight off the session
 * (`staff.wallet_balance`) — no request of its own.
 *
 * The session payload is already fetched once per page, shared by every
 * consumer and refreshed on focus, so the header, the private-area banner and
 * the checkout all show the same number without a single extra call. The
 * dedicated endpoint stays for the wallet page alone, which needs the buckets
 * the session does not carry (`useWalletDetails`).
 *
 * The spendable number here is `available`, which is what the API docs point at
 * for header display — the wallet endpoint names that same idea `accounting`.
 */
const useWallet = () => {
  const user = useLoggedUser();
  const hasWallet = canAccessWallet(user);
  const walletBalance = user?.staff?.wallet_balance;
  const purchaseBalance = toAmount(walletBalance?.available);

  return {
    hasWallet,
    /** Spendable amount (`available`) — see the note above. */
    purchaseBalance,
    /**
     * Wallet currency in the shape the formatter takes. The session sends a
     * bare symbol where the wallet endpoint sends a whole `Currency` object, and
     * wrapping it here keeps that difference out of the components.
     */
    currency: walletBalance?.currency
      ? { symbol: walletBalance.currency }
      : undefined,
    /**
     * Whether wallet UI should be rendered at all. Two conditions, both
     * required: a *confirmed* answer about this company — `useLoggedUser()`
     * returns `undefined` while the session is in flight and `null` when it
     * failed, and both count as "no wallet" — and a balance in the payload, so a
     * company whose `wallet_balance` is missing (no PHC, origin without a
     * wallet) shows nothing instead of a placeholder or a zero.
     */
    canDisplay: hasWallet && purchaseBalance !== undefined,
  };
};

/**
 * Full wallet payload from `GET /accounts/company/wallet-balance`: the buckets
 * only the wallet page shows (`accounting`, `held`, `earned_total`, …).
 *
 * Reserved for that page on purpose — everywhere else reads the balance off the
 * session (`useWallet`), so this endpoint is hit once per visit to the wallet
 * instead of on every render of the header.
 */
export const useWalletDetails = () => {
  const { locale = 'en' } = useRouter();
  const hasWallet = useWalletAccess();

  const { data: balance, isLoading, isFetching } = useQuery(
    ...getWalletBalance({ locale }),
    {
      enabled: hasWallet,
      keepPreviousData: true,
      refetchOnMount: 'always',
      refetchOnWindowFocus: 'always',
    }
  );

  return {
    hasWallet,
    balance,
    purchaseBalance: getPurchaseBalance(balance),
    isLoading: hasWallet && isLoading,
    isFetching,
  };
};

export default useWallet;
